import { apiService } from '@/services/apiService'
import { resolveApiResourceUrl } from '@/services/apiUrlService'
import { connectivityService } from '@/services/connectivityService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { usePhotoStore } from '@/stores/photoStore'
import type { Photo } from '@/types/photo'

export type PhotoAssetVariant = 'full' | 'thumb'

export const PHOTO_ASSET_URL_EXPIRY_OFFSET_MS = 30 * 1000

type EnsurePhotoAssetUrlOptions = {
  variants?: PhotoAssetVariant[]
  now?: number
  offsetMs?: number
}

const inFlightRefreshes = new Map<string, Promise<boolean>>()

function parseAmzDate(value: string): number | null {
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(value)
  if (!match) return null

  const [, year, month, day, hour, minute, second] = match
  return Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  )
}

export function getSignedUrlExpirationMs(url: string | null | undefined): number | null {
  if (!url || url.startsWith('data:') || url.startsWith('blob:')) {
    return null
  }

  const expiresMatch = /(?:[?&])expires=(\d+)/.exec(url)
  if (expiresMatch) {
    const expiresSeconds = Number(expiresMatch[1])
    return Number.isFinite(expiresSeconds) ? expiresSeconds * 1000 : null
  }

  try {
    const baseUrl = typeof window === 'undefined' ? 'https://route.camp' : window.location.href
    const parsed = new URL(url, baseUrl)
    const amzDate = parsed.searchParams.get('X-Amz-Date') ?? parsed.searchParams.get('x-amz-date')
    const amzExpires =
      parsed.searchParams.get('X-Amz-Expires') ?? parsed.searchParams.get('x-amz-expires')

    if (amzDate && amzExpires) {
      const issuedAt = parseAmzDate(amzDate)
      const ttlSeconds = Number(amzExpires)
      if (issuedAt !== null && Number.isFinite(ttlSeconds)) {
        return issuedAt + ttlSeconds * 1000
      }
    }
  } catch {
    return null
  }

  return null
}

export function isSignedUrlExpiring(
  url: string | null | undefined,
  offsetMs = PHOTO_ASSET_URL_EXPIRY_OFFSET_MS,
  now = Date.now(),
): boolean {
  const expirationMs = getSignedUrlExpirationMs(url)
  return expirationMs !== null && expirationMs - now <= offsetMs
}

function getPhotoAssetUrl(photo: Photo, variant: PhotoAssetVariant): string | null {
  if (variant === 'full') {
    return photo._binaryDataUrl || null
  }

  return photo._thumbnailUrl || photo._binaryDataUrl || null
}

function hasExpiringPhotoAssetUrl(
  photos: Photo[],
  variants: PhotoAssetVariant[],
  offsetMs: number,
  now: number,
): boolean {
  for (const photo of photos) {
    for (const variant of variants) {
      const url = getPhotoAssetUrl(photo, variant)
      if (isSignedUrlExpiring(url, offsetMs, now)) {
        return true
      }
    }
  }

  return false
}

function applyFreshUrlsToPhotos(
  photos: Photo[],
  freshUrls: Map<string, { url: string | null; thumbnailUrl: string | null }>,
): boolean {
  let changed = false

  for (const photo of photos) {
    const fresh = freshUrls.get(photo.clientId)
    if (!fresh) continue

    const nextUrl = resolveApiResourceUrl(fresh.url) ?? fresh.url
    const nextThumbnailUrl = resolveApiResourceUrl(fresh.thumbnailUrl) ?? fresh.thumbnailUrl

    if (nextUrl && photo._binaryDataUrl !== nextUrl) {
      photo._binaryDataUrl = nextUrl
      photo._resolvedOfflineBinaryUrl = null
      changed = true
    }

    if (nextThumbnailUrl && photo._thumbnailUrl !== nextThumbnailUrl) {
      photo._thumbnailUrl = nextThumbnailUrl
      photo._resolvedOfflineThumbnailUrl = null
      changed = true
    }
  }

  return changed
}

export async function refreshWorkspacePhotoAssetUrls(
  workspaceId: string,
  photos: Photo[] = usePhotoStore().photos,
): Promise<boolean> {
  if (!workspaceId || !connectivityService.isEffectivelyOnline.value) {
    return false
  }

  const existingRefresh = inFlightRefreshes.get(workspaceId)
  if (existingRefresh) {
    return existingRefresh
  }

  const refreshPromise = apiService
    .getWorkspacePhotoAssetUrls(workspaceId)
    .then((response) => {
      const freshUrls = new Map<string, { url: string | null; thumbnailUrl: string | null }>(
        response.photos.map(
          (photo) =>
            [
              photo.clientId,
              {
                url: photo.url,
                thumbnailUrl: photo.thumbnailUrl,
              },
            ] as const,
        ),
      )

      const photoStore = usePhotoStore()
      const storeChanged = applyFreshUrlsToPhotos(photoStore.photos, freshUrls)
      const providedChanged =
        photos === photoStore.photos ? false : applyFreshUrlsToPhotos(photos, freshUrls)
      return storeChanged || providedChanged
    })
    .catch((error) => {
      logService.warn(LOG_CATEGORIES.API, 'Failed to refresh photo asset URLs', {
        workspaceId,
        error,
      })
      return false
    })
    .finally(() => {
      inFlightRefreshes.delete(workspaceId)
    })

  inFlightRefreshes.set(workspaceId, refreshPromise)
  return refreshPromise
}

export async function ensureWorkspacePhotoAssetUrlsFresh(
  workspaceId: string,
  photos: Photo[],
  options: EnsurePhotoAssetUrlOptions = {},
): Promise<boolean> {
  const variants = options.variants ?? ['full', 'thumb']
  const offsetMs = options.offsetMs ?? PHOTO_ASSET_URL_EXPIRY_OFFSET_MS
  const now = options.now ?? Date.now()

  if (!hasExpiringPhotoAssetUrl(photos, variants, offsetMs, now)) {
    return false
  }

  return refreshWorkspacePhotoAssetUrls(workspaceId, photos)
}
